<template>
  <section v-if="!isLoading" class="subscriptions">
    <CTAActions
      v-if="showCtaActions"
      v-bind="{
        userCurrentPlan,
        premiumPlanCodes,
        annualPlanCode,
        monthlyPlanCode,
      }"
      @show-premium-upgrade-modal="handlePremiumUpgradeFromCTA"
      @show-upgrade-modal="isUpgradeModalShown = true"
      @cancel-subscription-renewal="handleCancelSubscriptionRenewal"
    />
    <div v-if="activePlans.length" class="subscriptions__active">
      <div class="subscriptions__title">
        <h6 class="dashboard__tab-title">My Subscription</h6>
      </div>
      <template v-for="subscription in activePlans">
        <SkillDivePlan
          v-if="
            subscription.plan_code === skillDiveAnnualCode ||
            subscription.plan_code === skillDivePtaCode
          "
          :key="subscription.id"
          v-bind="{
            subscription,
            isLoading: isActionLoading,
            validAddons,
          }"
          @cancel-subscription-renewal="handleCancelSubscriptionRenewal"
          @reactivate-subscription-renewal="handleReactivateSubscriptionRenewal"
          @manage-skill-dive-clicked="isManageSkillDiveModalShown = true"
        />
        <ActivePlan
          v-else
          :key="subscription.id"
          v-bind="{
            subscription,
            accountCredit,
            isLoading: isActionLoading,
            newPlanCodes,
            annualPlanCode,
          }"
          @cancel-subscription-renewal="handleCancelSubscriptionRenewal"
          @reactivate-subscription-renewal="handleReactivateSubscriptionRenewal"
          @upgrade-plan="handlePlanUpgrade"
          @is-managing-payment="$emit('is-managing-payment')"
        />
      </template>
    </div>
    <NoPlans v-else :plansLink="plansLink" />
    <div v-if="inactivePlans.length" class="subscriptions__inactive">
      <div class="subscriptions__title">
        <h6 class="dashboard__tab-sub-title">Inactive Subscriptions</h6>
        <InactivePlan
          v-for="subscription in inactivePlans"
          :key="subscription.id"
          :subscription="subscription"
        />
      </div>
    </div>
    <div v-if="invoices.length" class="subscriptions__invoicing">
      <BillingHistory :invoices="invoices" />
    </div>
    <UpgradePlanModal
      v-if="isUpgradeModalShown && canUpgradeUserPlan"
      :subscriptionId="subscriptionIdForPlanUpgrade || activePlans[0].id"
      :couponCode="couponCodeForPremium"
      :isPremiumOnlyModal="isPremiumUpgradeModalShown"
      @toggle-upgrade-modal="isUpgradeModalShown = !isUpgradeModalShown"
    />
    <CancelSubscriptionRenewalModal
      v-if="isCancelSubscriptionModalShown"
      :subscription="subscriptionForCancellation"
      :cancellationFields="cancellationFields"
      @close-cancel-subscription-modal="handleCloseCancelSubscriptionModal"
    />
    <ManageSkillDiveModal
      v-if="isManageSkillDiveModalShown"
      :validAddons="validAddons"
      :isLoading="isActionLoading"
      :userCurrentPlan="userCurrentPlan"
      @close-manage-skill-dive-modal="isManageSkillDiveModalShown = false"
      @save-skill-modal-changes="handleSkillDiveModalChanges"
    />
  </section>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import CONSTANTS from '@/constants'
import UpgradePlanModal from '@/components/modals/UpgradePlanModal.vue'
import CancelSubscriptionRenewalModal from '@/components/modals/CancelSubscriptionRenewalModal.vue'
import ManageSkillDiveModal from '@/components/modals/ManageSkillDiveModal.vue'
import { subscriptionsCommons } from '@/helpers/mixins'
import NoPlans from './subscriptions/NoPlans.vue'
import ActivePlan from './subscriptions/ActivePlan.vue'
import CTAActions from './subscriptions/CTAActions.vue'
import BillingHistory from './subscriptions/BillingHistory.vue'
import InactivePlan from './subscriptions/InactivePlan.vue'
import SkillDivePlan from './subscriptions/SkillDivePlan.vue'

export default {
  name: 'Subscriptions',
  components: {
    CTAActions,
    ActivePlan,
    BillingHistory,
    InactivePlan,
    UpgradePlanModal,
    CancelSubscriptionRenewalModal,
    NoPlans,
    SkillDivePlan,
    ManageSkillDiveModal,
  },
  mixins: [subscriptionsCommons],
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isActionLoading: false,
      isCancelSubscriptionModalShown: false,
      subscriptionForCancellation: {},
      subscriptionIdForPremium: '',
      isManageSkillDiveModalShown: false,
      skillDiveAnnualCode: CONSTANTS.SKILL_DIVE.SKILL_DIVE_ANNUAL,
      skillDivePtaCode: CONSTANTS.SKILL_DIVE.SKILL_DIVE_PTA,
      annualPlanCode: CONSTANTS.PLANS.ANNUAL,
      monthlyPlanCode: CONSTANTS.PLANS.MONTHLY,
    }
  },
  computed: {
    ...mapState({
      invoices: state => state.subscriptions.currentInvoices,
      newPlanCodes: state => state.subscriptions.newPlanCodes,
      paymentInfo: state => state.subscriptions.paymentInfo,
      cancellationFields: state => state.subscriptions.cancellationFields,
      plansLink: state => state.app.plansLink,
      validAddons: state => state.subscriptions.validAddons,
    }),
    inactivePlans() {
      const {
        usersSubscriptions: { inactivePlans },
      } = this
      return inactivePlans
    },
    accountCredit() {
      const {
        paymentInfo: { account_credit },
      } = this
      return account_credit
    },
    userCurrentPlan() {
      const { activePlans } = this
      const activeState = CONSTANTS.SUBSCRIPTIONS.ACTIVE_STATE
      if (activePlans.length > 0) {
        const hasPremium = activePlans.find(
          plan =>
            Object.values(CONSTANTS.PLANS.PREMIUM_PLANS).includes(
              plan.plan_code
            ) && plan.state === activeState
        )
        const hasAnnual = activePlans.find(
          plan =>
            plan.plan_code === this.annualPlanCode && plan.state === activeState
        )
        const hasMonthly = activePlans.find(
          plan =>
            plan.plan_code === this.monthlyPlanCode &&
            plan.state === activeState
        )
        const skillDiveSubscription = activePlans.find(
          plan =>
            plan.plan_code === CONSTANTS.SKILL_DIVE.SKILL_DIVE_ANNUAL &&
            plan.state === activeState
        )
        if (hasPremium) {
          return {
            plan_code: hasPremium.plan_code,
            plan: hasPremium,
            skill_dive_subscription: skillDiveSubscription,
          }
        }
        if (hasAnnual) {
          return {
            plan_code: this.annualPlanCode,
            plan: hasAnnual,
            skill_dive_subscription: skillDiveSubscription,
          }
        }
        if (hasMonthly) {
          return {
            plan_code: this.monthlyPlanCode,
            plan: hasMonthly,
            skill_dive_subscription: skillDiveSubscription,
          }
        }

        const currentPlan = activePlans.find(
          plan => plan.valid_plans?.length && plan.state === activeState
        )
        return {
          plan_code: currentPlan?.plan_code || '',
          plan: currentPlan,
          skill_dive_subscription: skillDiveSubscription,
        }
      }
      return { plan_code: '' }
    },
    showCtaActions() {
      return this.userCurrentPlan?.plan && this.canUpgradeUserPlan
    },
    premiumPlanCodes() {
      return CONSTANTS.PLANS.PREMIUM_PLANS
    },
    canUpgradeUserPlan() {
      return !!this.validPlans?.length && this.isUpgradePlanAllowed
    },
  },
  async created() {
    const currentSubscriptionId = this.userCurrentPlan?.plan?.id
    const currentSkillDiveSubscriptionId =
      this.userCurrentPlan?.skill_dive_subscription?.id
    if (currentSubscriptionId && !this.isUpgradePlanAllowed) {
      await this.$store.dispatch(
        'subscriptions/getValidPlans',
        currentSubscriptionId
      )
      this.$store.dispatch('subscriptions/isUpgradePlanAllowed', true)
    }
    await this.$store.dispatch('subscriptions/getAllInvoices')
    if (currentSkillDiveSubscriptionId) {
      await this.$store.dispatch(
        'subscriptions/getValidAddonsForSubscription',
        currentSkillDiveSubscriptionId
      )
      // Need to update Skill Dive remaining time
      await this.$store.dispatch('auth/userMeOnContent')
    }
    this.$store.dispatch('app/setIsComponentLoading', false)
    const coupon = new URLSearchParams(window.location.search).get('coupon')
    if (coupon && coupon.length) {
      this.checkForPremiumPromo()
    }
  },
  methods: {
    ...mapActions('auth', ['userMeOnContent']),
    ...mapActions('subscriptions', [
      'reactivateSubscription',
      'addAddonsForSubscription',
      'getValidAddonsForSubscription',
      'skillDiveAddHoursPurchase',
    ]),
    handleCloseCancelSubscriptionModal() {
      this.isCancelSubscriptionModalShown = false
      this.subscriptionForCancellation = {}
    },
    handleCancelSubscriptionRenewal(subscription) {
      this.subscriptionForCancellation = subscription
      this.isCancelSubscriptionModalShown = true
    },
    async handleReactivateSubscriptionRenewal({ id }) {
      this.isActionLoading = true
      await this.reactivateSubscription(id)
      this.isActionLoading = false
    },
    handlePremiumUpgradeFromCTA() {
      const { activePlans } = this
      const activeState = CONSTANTS.SUBSCRIPTIONS.ACTIVE_STATE
      activePlans.find(
        plan =>
          plan.plan_code === this.annualPlanCode && plan.state === activeState
      )
      this.handlePlanUpgrade(this.userCurrentPlan?.plan)
    },
    async checkForPremiumPromo() {
      const {
        usersSubscriptions: { personalPlans },
      } = this
      if (personalPlans.length) {
        const activePlan = personalPlans.find(plan => plan.state === 'active')
        if (activePlan) {
          this.handlePlanUpgrade(activePlan)
        } else {
          const cancelledPlan = personalPlans.find(
            plan => plan.state === 'cancelled'
          )
          if (cancelledPlan) {
            const cancelledPlanToastMessage =
              'To qualify for this promotion your subscription must be active.<br /> Would you like to reactivate your subscription so that you can <br />take advantage of the promotional pricing?'
            this.createPromoToast(cancelledPlanToastMessage, cancelledPlan)
          }
        }
      }
    },
    async createPromoToast(message, cancelledPlan) {
      this.$toasted.info(message, {
        theme: 'outline',
        iconPack: 'material',
        position: 'top-center',
        icon: '',
        className: 'subscriptions__toast--tmp',
        action: [
          {
            text: 'Yes!',
            onClick: async (e, toastObject) => {
              toastObject.goAway(0)
              this.handleCancelledPromo(cancelledPlan)
            },
            class: 'subscriptions__toast--tmpaction',
          },
          {
            text: 'Cancel',
            onClick: (e, toastObject) => {
              toastObject.goAway(0)
            },
            class: 'subscriptions__toast--tmpaction',
          },
        ],
      })
    },
    async handleCancelledPromo({ id, plan_code }) {
      await this.handleReactivateSubscriptionRenewal({ id })
      await this.handlePlanUpgrade({ id, plan_code })
    },
    async handleSkillDiveModalChanges() {
      this.isActionLoading = true
      const skillDiveSubscriptionId =
        this.userCurrentPlan?.skill_dive_subscription?.id
      await this.getValidAddonsForSubscription(skillDiveSubscriptionId)
      this.isActionLoading = false
    },
  },
}
</script>

<style lang="scss">
.subscriptions {
  margin: 50px 0 0 0;
  max-width: 1000px;
  &__inactive {
    margin-top: 60px;
  }
  &__background {
    position: absolute;
    height: 100%;
    width: 100%;
    max-width: 1920px;
    z-index: -1;
    top: 255px;
    left: 0;
    right: 0;
    margin: 0 auto;
  }
  &__cta {
    color: var(--white);
    font-family: var(--font-bold);
    font-size: rem(29px);
    line-height: 2.5rem;
    &-wrapper {
      background-size: cover;
      background-image: url('../../assets/images/promotions/add-year-mobilebanner.png');
      background-position: center;
      @include tablet {
        background-image: url('../../assets/images/promotions/add-year-tabletbanner.png');
      }
      &--trial {
        background-image: url('../../assets/images/subscriptions/free-trial-mobile.png');
        @include tablet {
          background-image: url('../../assets/images/subscriptions/free-trial.png');
        }
      }
    }
  }
  &__action {
    background: transparent;
    border: none;
    color: var(--white);
    text-decoration: underline;
    padding: 0;
    margin: 0;
    cursor: pointer;
    &:hover {
      color: var(--orange);
    }
  }
  &__error {
    margin: 25px 0;
    color: var(--gray);
    font-weight: 400;
    display: block;
    text-align: center;
    font-size: (24 / 16) * 1rem;
    @media (min-width: 769px) {
      margin: 45px 0;
    }
  }
  &__invoicing {
    margin: 40px 0;
  }
  &__break {
    display: none;
    @include mobile {
      display: block;
    }
  }
  &__toast {
    &--tmp {
      padding: 20px !important;
      font-size: rem(18px) !important;
      font-family: var(--font-bold) !important;
    }
    &--tmpaction {
      font-family: var(--font-bold) !important;
      color: var(--green) !important;
      text-decoration: underline !important;
      &:nth-of-type(2) {
        color: var(--red) !important;
        opacity: 0.5;
        text-decoration: none !important;
      }
    }
  }
}
</style>
